import * as _errors from '@attain-apps/errors'
import { environment } from '@attain-apps/fe/web/main/env'
import type * as transformers from '@attain-apps/transformers'
import * as awsAmplifyApi from 'aws-amplify/api'
import * as awsAmplifyAuth from 'aws-amplify/auth'
import { createClient } from 'graphql-ws'
import type * as graphql from 'graphql'
import * as rxjs from 'rxjs'

import type { OUT } from './transformers'

const client = awsAmplifyApi.generateClient()

/** graphql-ws client used when running against the local mock server. */
const _localWsClient = environment.wsEndpoint
  ? createClient({ url: environment.wsEndpoint })
  : null

/**
 * Subscribes to a GraphQL subscription and returns an Observable that emits
 * transformed results as they arrive from AWS AppSync.
 */
export function subscribeGql<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>({
  variables,
  query,
  transformer,
}: subscribeGql.Params<
  TGqlResult,
  TReturn,
  TVariables
>): rxjs.Observable<TReturn> {
  // Local dev: use graphql-ws client directly (mock server uses ws://, which
  // Amplify cannot reach because it upgrades http:// → wss:// requiring TLS).
  if (_localWsClient) {
    return new rxjs.Observable<TReturn>(subscriber => {
      const queryString =
        typeof query === 'string' ? query : (query.loc?.source.body ?? '')
      const unsubscribe = _localWsClient.subscribe<TGqlResult>(
        { query: queryString, variables: variables as Record<string, unknown> },
        {
          complete: () => subscriber.complete(),
          error: error => subscriber.error(_errors.makeUserFriendly(error)),
          next: value => {
            if (!value.data) return
            try {
              subscriber.next(transformer.getEntityFrom(value.data))
            } catch (error) {
              subscriber.error(_errors.makeUserFriendly(error))
            }
          },
        },
      )
      return () => unsubscribe()
    })
  }

  return new rxjs.Observable<TReturn>(subscriber => {
    let subscriptionHandle: rxjs.Subscription | null = null

    awsAmplifyAuth
      .fetchAuthSession()
      .then(session => {
        const authToken = session.tokens?.accessToken.toString()
        if (!authToken) {
          throw new Error('Unable to get current user session')
        }

        // NOTE: AWS Amplify's subscription types are complex and don't properly infer
        // the subscribe method, so we cast to a simpler interface that matches runtime behavior
        const subscription = client.graphql({
          authToken,
          // NOTE: Amplify does not use the same version of GraphQL as us, so we need
          // to cast to their version
          query: query satisfies string | graphql.DocumentNode as
            | string
            | import('../../../../../../../../../node_modules/.pnpm/graphql@15.8.0/node_modules/graphql/language/ast').DocumentNode,
          variables: variables as never,
        }) as {
          subscribe: (observer: {
            error: (error: unknown) => void
            next: (value: { data: TGqlResult }) => void
          }) => rxjs.Subscription
        }

        subscriptionHandle = subscription.subscribe({
          error: (error: unknown) => {
            console.warn(
              `Subscription error: "${
                typeof query === 'string'
                  ? query
                  : `${_getOperationType(query)} ${_getOperationName(query)}`
              }"`,
              error,
            )
            subscriber.error(_errors.makeUserFriendly(error))
          },
          next: (value: { data: TGqlResult }) => {
            try {
              const transformed = transformer.getEntityFrom(
                value.data satisfies TGqlResult | undefined,
              )
              subscriber.next(transformed)
            } catch (error) {
              console.warn(
                `Failed to transform subscription data: "${
                  typeof query === 'string'
                    ? query
                    : `${_getOperationType(query)} ${_getOperationName(query)}`
                }"`,
                error,
              )
              subscriber.error(_errors.makeUserFriendly(error))
            }
          },
        })
      })
      .catch(error => {
        console.warn('Failed to establish subscription', error)
        subscriber.error(_errors.makeUserFriendly(error))
      })

    // Cleanup function when observable is unsubscribed
    return () => {
      subscriptionHandle?.unsubscribe()
    }
  })
}

export namespace subscribeGql {
  export interface Params<
    TGqlResult,
    TReturn,
    TVariables extends object = never,
  > {
    query: string | graphql.DocumentNode
    transformer: transformers.Transformer<
      transformers.Factory.OutputType<typeof OUT>,
      TGqlResult,
      TReturn
    >
    variables?: TVariables
  }
}

/**
 * Creates a function that can be used to subscribe to the GraphQL subscription given to
 * this function
 */
export function createSubscriber<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>(
  params: Readonly.Deep<createSubscriber.Params<TGqlResult, TReturn>>,
): Partial<TVariables> extends TVariables
  ? // If all of the variables are optional, then we can make variables as a whole
    // optional
    (variables?: TVariables) => rxjs.Observable<TReturn>
  : // Otherwise, we must require variables
    (variables: TVariables) => rxjs.Observable<TReturn>
export function createSubscriber<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>({
  query,
  transformer,
}: Readonly.Deep<createSubscriber.Params<TGqlResult, TReturn>>) {
  return (variables?: TVariables) =>
    subscribeGql({ query, transformer, variables })
}
export namespace createSubscriber {
  export type Params<TGqlResult, TReturn> = Omit<
    subscribeGql.Params<TGqlResult, TReturn>,
    'variables'
  >
}

/** Gets the operation name from a GraphQL DocumentNode */
function _getOperationName(
  document: Readonly.Deep<graphql.DocumentNode>,
): string | undefined {
  return (
    document.definitions.find(
      it => it.kind === 'OperationDefinition',
    ) as graphql.OperationDefinitionNode
  )?.name?.value
}

/** Gets the operation type from a GraphQL DocumentNode */
function _getOperationType(
  document: Readonly.Deep<graphql.DocumentNode>,
): graphql.OperationTypeNode | undefined {
  return (
    document.definitions.find(
      it => it.kind === 'OperationDefinition',
    ) as graphql.OperationDefinitionNode
  ).operation
}
